import React, { Fragment, useContext } from 'react'
import { Link } from 'react-router-dom'

import { Banner, Card, Grid, GridContainer, Icon, List, Row, Text } from '@plurall/elo-beta'
import { IntegrationAttentionBanner } from 'App/components/IntegrationAttentionBanner'
import Separator from 'App/components/Separator/Separator'
import PropTypes from 'prop-types'
import UserContext from 'stores/UserStore'

import {
  canChangeSchoolClassesAndLevels,
  canChangeSchoolRoles,
  ROLE_ADMIN_ID,
  ROLE_ATTENDANT_ID,
  ROLE_COORDINATOR_ID,
  ROLE_DIRECTOR_ID,
  ROLE_STATUS_DELETED_ID,
  ROLE_STATUS_INACTIVE_ID,
  ROLE_TEACHER_ID,
} from 'utils'
import { ROUTES } from 'utils/constants'

import { RoleStatus } from './RoleStatus'
import styles from './SchoolData.module.scss'

const SchoolRoles = ({ currentSchool, ...rest }) => {
  const userStore = useContext(UserContext)
  const { roles, byIntegration, brands } = currentSchool

  const isAllowedToChangeSchoolRoles = canChangeSchoolRoles({
    byIntegration,
    schoolBrands: brands,
    userExternalReference: userStore.externalReference,
    isUserByIntegration: userStore.isUserByIntegration,
  })

  const isAllowedToChangeSchoolClassesAndLevels = canChangeSchoolClassesAndLevels({
    byIntegration,
    schoolBrands: brands,
    userExternalReference: userStore.externalReference,
    isUserByIntegration: userStore.isUserByIntegration,
  })

  return (
    <GridContainer>
      <IntegrationAttentionBanner
        byIntegration={byIntegration}
        userExternalReference={userStore.externalReference}
        isUserByIntegration={userStore.isUserByIntegration}
      />

      <Row>
        <Grid desktop={12}>
          <Card>
            <div className={styles['card-wrapper']}>
              <SchoolRole canShowLink={isAllowedToChangeSchoolRoles} />

              {validateRole(roles, ROLE_TEACHER_ID) && (
                <RoleProfile
                  roleName='Professor'
                  role={getRole(roles, ROLE_TEACHER_ID)}
                  currentSchool={currentSchool}
                />
              )}
              {validateRole(roles, ROLE_COORDINATOR_ID) && (
                <RoleProfile
                  roleName='Coordenador'
                  role={getRole(roles, ROLE_COORDINATOR_ID)}
                  currentSchool={currentSchool}
                />
              )}
              {validateRole(roles, ROLE_DIRECTOR_ID) && (
                <RoleProfile
                  roleName='Diretor'
                  role={getRole(roles, ROLE_DIRECTOR_ID)}
                  currentSchool={currentSchool}
                />
              )}
              {validateRole(roles, ROLE_ADMIN_ID) && (
                <RoleProfile
                  roleName='Administrador'
                  role={getRole(roles, ROLE_ADMIN_ID)}
                  currentSchool={currentSchool}
                />
              )}

              {validateRole(roles, ROLE_ATTENDANT_ID) && (
                <RoleProfile
                  roleName='Atendente'
                  role={getRole(roles, ROLE_ATTENDANT_ID)}
                  currentSchool={currentSchool}
                />
              )}
            </div>
          </Card>
        </Grid>
      </Row>

      {validateRole(roles, ROLE_TEACHER_ID) && (
        <Row>
          <Grid desktop={12}>
            <TeacherProfile
              role={getRole(roles, ROLE_TEACHER_ID)}
              currentSchool={currentSchool}
              canShowLink={isAllowedToChangeSchoolClassesAndLevels}
              {...rest}
            />
          </Grid>
        </Row>
      )}
      {validateRole(roles, ROLE_COORDINATOR_ID) && (
        <Row>
          <Grid desktop={12}>
            <CoordinatorProfile
              role={getRole(roles, ROLE_COORDINATOR_ID)}
              canShowLink={isAllowedToChangeSchoolClassesAndLevels}
            />
          </Grid>
        </Row>
      )}
    </GridContainer>
  )
}

const validateRole = (roles, id) =>
  roles?.some(
    role =>
      role.id === id.toString() &&
      role.schools &&
      role.schools?.some(
        ({ roleStatus }) =>
          roleStatus.id !== ROLE_STATUS_INACTIVE_ID.toString() &&
          roleStatus.id !== ROLE_STATUS_DELETED_ID.toString(),
      ),
  )

const getRole = (roles, id) => roles.find(role => role.id === id.toString())
const getRoleStatus = (role, currentSchool) => {
  const school = role.schools?.find(school => school.id.toString() === currentSchool.id.toString())
  return +school?.roleStatus?.id
}

const getSchoolLevels = (roleLevels, schoolLevels) => {
  const levels = schoolLevels.map(({ id, name }) => ({ id, name }))
  roleLevels.forEach(item => {
    const index = levels.findIndex(lev => item.id.toString() === lev.id.toString())
    levels[index] = item
  })

  return levels
}

const getClasses = (knowlegedAreas, disciplines) => {
  const getDisciplinesByKnowlegedAreas = knowlegedAreas
    .filter(knowlegedAreas => knowlegedAreas.disciplines.length)
    .map(({ disciplines, gradeName }) => disciplines.map(item => ({ ...item, gradeName })))
    .flat()

  const filter = []
  getDisciplinesByKnowlegedAreas.forEach(discipline => {
    const findDiscipline = disciplines.find(values => values.id === discipline.id)
    if (findDiscipline) {
      discipline.classes.forEach(teste => {
        const findClass = findDiscipline.classes.find(current => teste.id === current.id)
        if (!findClass) {
          filter.push(discipline)
        }
      })
    } else {
      filter.push(discipline)
    }
  })

  return filter
}

const getClassesAndDisciplines = ({ disciplines, grades }) => {
  if (!disciplines && !grades) {
    return []
  }

  const filterDisciplines = disciplines.filter(discipline => !!discipline.classes.length)

  const knowlegedAreas = grades
    .map(grade => grade.knowlegedAreas.map(item => ({ ...item, gradeName: grade.name })))
    .flat()
  const filterKnowlegedAreas = getClasses(knowlegedAreas, disciplines)
  return orderClasses([...filterDisciplines, ...filterKnowlegedAreas])
}

const orderClasses = values => {
  const classesValues = values.flatMap(discipline =>
    discipline.classes.map(item => ({ disciplineName: discipline.name, ...item })),
  )

  const klasses = classesValues.map(item => ({
    gradeName: item.gradeName || (item.levelGrades && item.levelGrades[0].gradeName),
    ...item,
  }))

  return klasses.sort(
    (a, b) =>
      a.name.localeCompare(b.name) ||
      a.disciplineName.localeCompare(b.disciplineName) ||
      a.gradeName.localeCompare(b.gradeName) ||
      a.period.year - b.period.year,
  )
}

const SchoolRole = ({ canShowLink }) => (
  <div className={styles['roles-wrapper']}>
    <div className={`${styles['role-wrapper']} ${styles.subtitle}`}>
      <Text size='t1' bold>
        Funções na Escola
      </Text>
      {canShowLink && (
        <Link to={ROUTES.PERFIL_DADOS_ESCOLARES_FUNCOES}>
          <Text size='t3' element='a' dataTestId='link-edit-role'>
            Editar função
          </Text>
        </Link>
      )}
    </div>
  </div>
)

const EmptyClass = () => (
  <>
    <div>
      <div className={styles['select-profile']}>
        <Text size='t3' shy>
          Nenhuma turma selecionada.
        </Text>
      </div>
    </div>
    <Separator hideLast />
  </>
)

const TeacherProfile = ({ role, currentSchool, canShowLink, ...rest }) => {
  const getLink = levelId => `${ROUTES.PERFIL_DADOS_ESCOLARES_FUNCOES_PROFESSOR_SEGMENTO}${levelId}`

  return (
    <Card>
      <div className={styles['card-wrapper']} id='teacherProfile'>
        <CoordinatorAndTeacherProfileHeader
          roleName='Professor • Turmas e disciplinas cadastradas'
          dataTestIdRole='teacher'
        />
        <Separator />
        <TeacherProfileContent
          wrapper={styles['teacher-classes-wrapper']}
          mapData={getSchoolLevels(role.levels, currentSchool.levels)}
          linkText='Editar turmas'
          linkTo={getLink}
          canShowLink={canShowLink}
          {...rest}
        />
      </div>
    </Card>
  )
}

const CoordinatorProfile = ({ role, canShowLink }) => (
  <Card>
    <div className={styles['card-wrapper']} id='coordinatorProfile'>
      <CoordinatorAndTeacherProfileHeader
        roleName='Coordenador • Segmentos cadastrados'
        linkText='Editar segmentos'
        linkTo={ROUTES.PERFIL_DADOS_ESCOLARES_FUNCOES_COORDENADOR}
        dataTestIdRole='coordinator'
        canShowLink={canShowLink}
      />
      <CoordinatorProfileContent
        wrapper={styles['coordinator-levels-wrapper']}
        mapData={role.levels}
      />
    </div>
  </Card>
)

const CoordinatorAndTeacherProfileHeader = ({
  dataTestIdRole,
  roleName,
  linkText,
  linkTo,
  canShowLink,
}) => (
  <div className={`${styles['coordinator-and-teacher-wrapper']} ${styles.subtitle}`}>
    <div className={styles['coordinator-and-teacher-icon']}>
      <Text size='t1' bold>
        {roleName}
      </Text>
    </div>
    {linkTo && canShowLink && (
      <Link to={linkTo}>
        <Text size='t3' element='a' dataTestId={`link-edit-${dataTestIdRole}`}>
          {linkText}
        </Text>
      </Link>
    )}
  </div>
)

const CoordinatorProfileContent = ({ wrapper, mapData }) => (
  <div className={wrapper}>
    <div className={styles.classesWrapper}>
      {mapData.length > 0 && (
        <List
          items={mapData.map(({ name: label }) => ({
            label,
            small: true,
          }))}
        />
      )}
    </div>
  </div>
)

const TeacherProfileContent = ({ wrapper, mapData, linkTo, linkText, hasClasses, canShowLink }) => (
  <div className={wrapper}>
    {!hasClasses && canShowLink && (
      <div className={styles.banner}>
        <Banner title='Selecione suas turmas e disciplinas:' variant='attention' secondary>
          <p>Para ter acesso aos conteúdos</p>
        </Banner>
      </div>
    )}

    <div className={styles.classesWrapper}>
      {mapData.length > 0 &&
        mapData.map(data => {
          const disciplines = getClassesAndDisciplines(data)

          return (
            <Fragment key={data.id}>
              <div className={`${styles['roles-wrapper']} ${styles['teacher-profile-wrapper']}`}>
                <div
                  className={`${styles['coordinator-and-teacher-wrapper']} ${styles['teacher-profile-segment']}`}
                >
                  <Text size='t2' bold key={`${data.name}`}>
                    {data.name}
                  </Text>
                  {canShowLink && (
                    <Link to={linkTo(data.id)}>
                      <Text
                        size='t3'
                        element='a'
                        dataTestId={`link-edit-classes-level-id-${data.id}`}
                      >
                        {linkText}
                      </Text>
                    </Link>
                  )}
                </div>

                {disciplines.length > 0 ? (
                  <List
                    items={disciplines.map(({ disciplineName, gradeName, name, period }) => ({
                      label: `${name} ${'\u2022'} ${disciplineName} ${'\u2022'} ${gradeName} ${'\u2022'} ${period.year} `,
                      small: true,
                    }))}
                  />
                ) : (
                  <EmptyClass />
                )}
              </div>
              <Separator hideLast />
            </Fragment>
          )
        })}
    </div>
  </div>
)

const RoleProfile = ({ roleName, role, currentSchool }) => (
  <div className={styles['roles-wrapper']}>
    <div className={styles['role-profile-wrapper']}>
      <div className={styles['role-profile-wrapper-bullet']}>
        <Icon type='Bullet' />
        <Text size='t3'>{roleName}</Text>
      </div>
      {+role.id === ROLE_TEACHER_ID || +role.id === ROLE_COORDINATOR_ID ? (
        <RoleAnchor roleID={+role.id} />
      ) : (
        <RoleStatus status={getRoleStatus(role, currentSchool)} roleID={+role.id} />
      )}
    </div>
  </div>
)

const RoleAnchor = ({ roleID }) => {
  const roles = {
    [ROLE_TEACHER_ID]: {
      link: '#teacherProfile',
      text: 'Ver turmas',
      name: 'teacher',
    },
    [ROLE_COORDINATOR_ID]: {
      link: '#coordinatorProfile',
      text: 'Ver segmentos',
      name: 'coordinator',
    },
  }
  const selectedRole = roles[roleID]
  return (
    <Text
      secondary
      element='a'
      size='t3'
      href={selectedRole.link}
      dataTestId={`link-anchor-${selectedRole.name}`}
    >
      {selectedRole.text}
    </Text>
  )
}

CoordinatorAndTeacherProfileHeader.propTypes = {
  dataTestIdRole: PropTypes.string.isRequired,
  roleName: PropTypes.string.isRequired,
  linkText: PropTypes.string,
  linkTo: PropTypes.string,
  canShowLink: PropTypes.bool,
}

CoordinatorProfileContent.propTypes = {
  wrapper: PropTypes.string.isRequired,
  mapData: PropTypes.array.isRequired,
}

TeacherProfileContent.propTypes = {
  wrapper: PropTypes.string.isRequired,
  mapData: PropTypes.array.isRequired,
  linkText: PropTypes.string.isRequired,
  linkTo: PropTypes.string.isRequired,
  hasClasses: PropTypes.bool.isRequired,
  canShowLink: PropTypes.bool,
}

TeacherProfile.propTypes = {
  role: PropTypes.object.isRequired,
  currentSchool: PropTypes.object.isRequired,
  canShowLink: PropTypes.bool,
}

CoordinatorProfile.propTypes = {
  role: PropTypes.object.isRequired,
  currentSchool: PropTypes.object,
  canShowLink: PropTypes.bool,
}

RoleProfile.propTypes = {
  roleName: PropTypes.string.isRequired,
  role: PropTypes.object.isRequired,
  currentSchool: PropTypes.object.isRequired,
}

SchoolRoles.propTypes = {
  currentSchool: PropTypes.object,
  userStore: PropTypes.object,
}

SchoolRole.propTypes = {
  canShowLink: PropTypes.bool,
}

RoleAnchor.propTypes = {
  roleID: PropTypes.number,
}

export { SchoolRoles }
